import React, { useEffect, useState, useRef, Fragment, memo, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Package, Star, X, Minus, MessageSquare, AlertCircle, Brain, DollarSign, BookX, Video, HardHat, Flame, Building2, CircuitBoard, Cpu, Settings, Target, TrendingUp, Award, GitBranch, Lightbulb, Headphones, ChevronLeft, ChevronRight, Eye, Ticket } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-31">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-32">
        <span className="font-medium text-gray-900 pr-4" data-id="element-33">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-34" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-35" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-36">
          <p className="text-gray-700 leading-relaxed" data-id="element-37">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-38">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-39">
        <div className="aspect-[3/4] relative" data-id="element-40">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" data-id="element-41" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-42">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-43">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-44">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-45" />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [urgencySpots, setUrgencySpots] = useState(15);
  const [upsellSpots, setUpsellSpots] = useState(() => {
    const saved = localStorage.getItem('upsellSpots');
    return saved ? Math.max(2, parseInt(saved, 10)) : 8;
  });
  const [mainBuyers, setMainBuyers] = useState(() => {
    const saved = localStorage.getItem('mainBuyers');
    return saved ? parseInt(saved, 10) : 3251;
  });
  const [mainSpots, setMainSpots] = useState(15);
  const [comboBuyers, setComboBuyers] = useState(() => {
    const saved = localStorage.getItem('comboBuyers');
    return saved ? parseInt(saved, 10) : 5162;
  });
  const [comboSpots, setComboSpots] = useState(7);
  // Persist buyers and upsellSpots to localStorage
  useEffect(() => {
    localStorage.setItem('mainBuyers', mainBuyers.toString());
  }, [mainBuyers]);
  useEffect(() => {
    localStorage.setItem('comboBuyers', comboBuyers.toString());
  }, [comboBuyers]);
  useEffect(() => {
    localStorage.setItem('upsellSpots', upsellSpots.toString());
  }, [upsellSpots]);
  useEffect(() => {
    // Viewers fluctuation (40-63 range)
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(63, prev + change));
      });
    }, 3000 + Math.random() * 4000);
    // Profesionales (social proof): increase 1-3 every 10s
    const mainBuyersInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setMainBuyers(prev => prev + increment);
    }, 10000);
    const comboBuyersInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setComboBuyers(prev => prev + increment);
    }, 10000);
    // Card 1 cupos (scarcity): decrease 1 every 15-40s, min 2
    const urgencySpotsInterval = setInterval(() => {
      setUrgencySpots(prev => Math.max(2, prev - 1));
    }, 15000 + Math.random() * 25000);
    // Upsell cupos (scarcity): decrease 1 every 120-180s, min 2
    const upsellSpotsInterval = setInterval(() => {
      setUpsellSpots(prev => Math.max(2, prev - 1));
    }, 120000 + Math.random() * 60000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(mainBuyersInterval);
      clearInterval(comboBuyersInterval);
      clearInterval(urgencySpotsInterval);
      clearInterval(upsellSpotsInterval);
    };
  }, []);
  return {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  };
}
// Current Date Hook
function useCurrentDate() {
  const [dateString, setDateString] = useState('');
  const [shortDate, setShortDate] = useState('');
  const [weekdayDate, setWeekdayDate] = useState('');
  useEffect(() => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    setDateString(now.toLocaleDateString('es-AR', options));
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    setShortDate(`${dd}/${mm}/${yyyy}`);
    // Weekday + day + month (e.g. "Lunes 16 de Marzo")
    const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    setWeekdayDate(`${weekdays[now.getDay()]} ${now.getDate()} de ${months[now.getMonth()]}`);
  }, []);
  return {
    dateString,
    shortDate,
    weekdayDate
  };
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-46">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-47">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-48">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-49">
          <p data-id="element-50">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-51">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-52">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-53" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-54">
          <X className="w-5 h-5" data-id="element-55" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-56">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-57">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-58" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-59">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-60" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-61" />
        {error && <p className="text-red-500 text-sm" data-id="element-62">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-63">
          Enviar reseña
        </button>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  } = useDynamicStats();
  const {
    dateString: currentDate,
    shortDate,
    weekdayDate
  } = useCurrentDate();
  const [headerVisible, setHeaderVisible] = useState(false);
  const [fearsVisible, setFearsVisible] = useState(false);
  const [problemVisible, setProblemVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [localReview, setLocalReview] = useState<{
    name: string;
    text: string;
    stars: number;
  } | null>(null);
  const [bonusIndex, setBonusIndex] = useState(0);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  const marqueeResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const isDragging = useRef(false);
  // Marquee pause/resume handlers
  const pauseMarquee = () => {
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Drag handlers for manual swipe
  const handleDragStart = (clientX: number) => {
    const container = marqueeContainerRef.current;
    if (!container) return;
    isDragging.current = true;
    dragStartX.current = clientX;
    dragScrollLeft.current = container.scrollLeft;
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
  };
  const handleDragMove = (clientX: number) => {
    if (!isDragging.current) return;
    const container = marqueeContainerRef.current;
    if (!container) return;
    const dx = clientX - dragStartX.current;
    container.scrollLeft = dragScrollLeft.current - dx;
  };
  const handleDragEnd = () => {
    isDragging.current = false;
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Argentine chat reviews pool (30+)
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la forma de laburar. Antes conectaba de memoria y ahora entiendo qué hace cada cable 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano, nada de vueltas teóricas al pedo. Los diagramas están claritos',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con un cliente. Tenía que automatizar un tanque y no le encontraba la vuelta. Con esto lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso hermano. Solo con la planilla de presupuesto ya se paga solo. La uso para todos los laburos',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la cabeza mal. Antes adivinaba, ahora sé lo que hago ⚡',
    time: '16:22',
    verified: true
  }, {
    name: 'Ricardo A.',
    msg: 'Soy técnico hace 10 pirulos y siempre se aprende algo nuevo. El protocolo de verificación es un caño, lo uso siempre',
    time: '20:11',
    verified: true
  }, {
    name: 'Marcelo D.',
    msg: 'Al principio me costó un toque porque venía de cero, pero leyéndolo tranqui se entiende todo. Me salvó con un tablero de bombas',
    time: '08:45',
    verified: false
  }, {
    name: 'Fernando T.',
    msg: 'Es técnico pero se deja leer. No es esos libros aburridos de ingeniería que te dormís. Es para el que está en la obra posta',
    time: '13:20',
    verified: true
  }, {
    name: 'Jorge L.',
    msg: 'Lo imprimí y lo llevo en la caja de herramientas. Cuando tengo una duda lo miro ahí nomás. Re práctico',
    time: '17:55',
    verified: true
  }, {
    name: 'Sergio F.',
    msg: 'Está muy completo che. Estaría bueno que tenga videos, pero las explicaciones escritas son un golazo igual',
    time: '10:30',
    verified: true
  }, {
    name: 'Diego P.',
    msg: 'Ni hablar, lo mejor que compré en años. Ahora los tableros no me asustan más jaja',
    time: '15:12',
    verified: true
  }, {
    name: 'Ramiro V.',
    msg: 'Capo el que armó esto. Se nota que sabe de campo, no es un teórico de escritorio. Re recomendado 👌',
    time: '19:40',
    verified: true
  }, {
    name: 'Hernán G.',
    msg: 'Arranqué a tomar laburos más grandes gracias a esto. Antes les tenía miedo a los tableros industriales',
    time: '07:55',
    verified: true
  }, {
    name: 'Leandro M.',
    msg: 'Posta que vale la pena. Mi viejo es electricista y se lo pasé, quedó re manija también',
    time: '12:18',
    verified: false
  }, {
    name: 'Cristian R.',
    msg: 'Lo que más me sirvió fue entender la lógica de los contactores. Antes los conectaba y rezaba jaja',
    time: '21:05',
    verified: true
  }, {
    name: 'Facundo L.',
    msg: 'Che re completo. Los bonos solos ya valen más que lo que pagué. Un golazo la planilla de presupuesto',
    time: '14:50',
    verified: true
  }, {
    name: 'Nicolás S.',
    msg: 'Soy de Córdoba y acá hay mucho laburo industrial. Con esto me animé a meterme en automatización. Gracias crack 🙌',
    time: '16:33',
    verified: true
  }, {
    name: 'Martín H.',
    msg: 'Lo arranqué a leer un domingo y no lo solté. Está explicado como si te lo contara un compañero de laburo',
    time: '11:47',
    verified: true
  }, {
    name: 'Walter B.',
    msg: 'Tengo 15 años en el rubro y hay cosas que no sabía. El tema de protecciones me voló la cabeza',
    time: '09:22',
    verified: true
  }, {
    name: 'Carlos A.',
    msg: 'Muy bueno loco. Lo único que le falta son más ejemplos de variadores, pero para mando es lo mejor que vi',
    time: '18:15',
    verified: false
  }, {
    name: 'Ezequiel D.',
    msg: 'Me lo recomendó un colega y la verdad que tenía razón. Re sirve para el día a día en obra',
    time: '13:08',
    verified: true
  }, {
    name: 'Damián F.',
    msg: 'Che, el bono de paneles solares es tremendo. Estoy pensando en meterme en eso también. Todo suma 🔋',
    time: '20:30',
    verified: true
  }, {
    name: 'Lucas T.',
    msg: 'Posta que cambió mi forma de presupuestar. Antes tiraba números al voleo, ahora tengo todo ordenado',
    time: '15:42',
    verified: true
  }, {
    name: 'Alejandro N.',
    msg: 'Lo mejor es que lo podés consultar en la obra. Nada de andar buscando en YouTube a las apuradas',
    time: '08:10',
    verified: true
  }, {
    name: 'Tomás R.',
    msg: 'Soy de Mendoza, laburo en bodegas. Los circuitos de mando de las bombas ahora los entiendo de verdad',
    time: '17:25',
    verified: true
  }, {
    name: 'Ignacio C.',
    msg: 'Un amigo me dijo "compralo que no te vas a arrepentir" y tenía razón. Muy claro todo',
    time: '12:55',
    verified: false
  }, {
    name: 'Santiago M.',
    msg: 'Re piola el material. Se nota que está hecho por alguien que labura en serio, no un profesor de facultad',
    time: '19:18',
    verified: true
  }, {
    name: 'Agustín V.',
    msg: 'Che lo del protocolo de verificación es genial. Ahora antes de dar tensión reviso todo como corresponde',
    time: '10:05',
    verified: true
  }, {
    name: 'Maximiliano P.',
    msg: 'Increíble la relación precio-calidad. Con lo que sale una herramienta tenés conocimiento para toda la vida',
    time: '14:40',
    verified: true
  }, {
    name: 'Gonzalo E.',
    msg: 'Lo compré medio desconfiado y me sorprendió. Está muy bien armado, se nota el laburo que le metieron 💯',
    time: '16:58',
    verified: true
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    return () => revealObserver.disconnect();
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    const handleScroll = () => {
      setHeaderVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'fears-section') setFearsVisible(true);
          if (entry.target.id === 'problem-section') setProblemVisible(true);
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['fears-section', 'problem-section', 'problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47885775995102:1';
  };
  const handleComboPurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47989263106270:1';
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) observer.observe(bonusSection);
    return () => observer.disconnect();
  }, [trackEvent]);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    setLocalReview(review);
  };
  const fearItems = [{
    icon: Flame,
    title: 'Tenés miedo de quemar un motor',
    desc: 'Un error de conexión y el equipo queda inutilizado.'
  }, {
    icon: AlertCircle,
    title: 'Cuando no responde',
    desc: 'Dás tensión… y el circuito no hace lo que debería.'
  }, {
    icon: Brain,
    title: 'La inseguridad te hace quedar mal frente al cliente',
    desc: 'No por el trabajo, sino por cómo lo enfrentás.'
  }, {
    icon: DollarSign,
    title: 'Estás estancado ganando siempre lo mismo',
    desc: 'porque te falta confianza para aceptar trabajos más grandes.'
  }];
  const problemItems = [{
    icon: BookX,
    title: 'Libros teóricos',
    desc: 'Mucho concepto, poca aplicación real.'
  }, {
    icon: Video,
    title: 'Videos aislados',
    desc: 'Sirven para un caso puntual, no cuando algo cambia.'
  }, {
    icon: HardHat,
    title: 'Experiencia sola',
    desc: 'Funciona… hasta que aparece un caso distinto.'
  }];
  const decisionItems = [{
    icon: CircuitBoard,
    text: 'Criterio claro para interpretar circuitos de mando',
    keyword: 'Criterio'
  }, {
    icon: Cpu,
    text: 'Lógica aplicada a motores y tableros reales',
    keyword: 'Lógica'
  }, {
    icon: Target,
    text: 'Menos dudas al montar y diagnosticar',
    keyword: 'diagnóstico'
  }, {
    icon: Settings,
    text: 'Decisiones técnicas, no intuición',
    keyword: 'Decisiones'
  }, {
    icon: Award,
    text: 'Material pensado para trabajo en campo',
    keyword: 'campo'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-64">
      {notification && <PurchaseNotification name={notification.name} city={notification.city} isVisible={isVisible} data-id="element-65" />}

      {/* Header - Hidden initially, appears on scroll */}
      <header className={`bg-blue-900 text-white py-3 fixed top-0 left-0 right-0 z-40 shadow-md transition-all duration-400 ${headerVisible ? 'animate-header-slide' : 'opacity-0 -translate-y-full pointer-events-none'}`} data-id="element-66">
        <div className="container mx-auto px-4 flex justify-between items-center" data-id="element-67">
          <div className="flex items-center gap-3" data-id="element-68">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse" data-id="element-69">
              <img src="https://cdn.magicpatterns.com/uploads/tA6D5wwQNzXrTuuBrNPdzP/capture_260206_165744.png" alt="Maestro Eléctrico Logo" className="w-full h-full object-cover" data-id="element-70" />
            </div>
            <span className="font-bold text-lg tracking-tight" data-id="element-71">
              Maestro Eléctrico
            </span>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded text-sm transition-colors" data-id="element-72">
            Ver Oferta
          </button>
        </div>
      </header>

      {/* TOP HERO IMAGE BANNER */}
      <div className="w-full leading-none" data-id="element-73">
        <img src="https://cdn.magicpatterns.com/uploads/8ZwogjuhWmBRejLtUpZZNA/Gemini_Generated_Image_9hkt5y9hkt5y9hkt.png" alt="Maestro Eléctrico Banner" className="w-full h-auto block" data-id="element-74" />
      </div>

      {/* HERO - Radial gradient with vibrant blue center */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-8" style={{
      background: 'radial-gradient(ellipse at 50% 50%, #1e40af 0%, #1e3a8a 25%, #172554 50%, #0f1729 80%, #0a1120 100%)'
    }} data-id="element-75">
        <div className="container mx-auto px-4 relative z-10" data-id="element-76">
          {/* Small text above title */}
          <p className="text-center text-[11px] md:text-xs font-medium text-blue-300/70 uppercase tracking-[0.25em] mb-6 animate-hero-text" style={{
          animationDelay: '0s'
        }} data-id="element-77">
            PARA ELECTRICISTAS, TÉCNICOS Y MANTENIMIENTO INDUSTRIAL
          </p>

          {/* Main Title - Staggered fade */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-[1.1] text-center" data-id="element-78">
            <span className="block animate-hero-text text-white" style={{
            animationDelay: '0.1s'
          }} data-id="element-79">
              ENTENDÉ EL
            </span>
            <span className="block animate-hero-text text-yellow-400" style={{
            animationDelay: '0.3s'
          }} data-id="element-80">
              CONTROL ELÉCTRICO
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-center mb-12 font-medium animate-hero-text text-blue-100" style={{
          animationDelay: '0.5s'
        }} data-id="element-81">
            DEJÁ DE IMPROVISAR Y TOMÁ{' '}
            <span className="text-yellow-300" data-id="element-82">TRABAJOS MEJOR PAGOS</span>
          </p>

          {/* Mockup - Premium with glow */}
          <div className="mb-10 flex justify-center relative animate-hero-zoom" style={{
          animationDelay: '0.7s'
        }} data-id="element-83">
            {/* Glow behind mockup */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-blue-500/30 rounded-full blur-[80px]" data-id="element-84"></div>
            <img src="https://cdn.magicpatterns.com/uploads/eBX26Xqu54fvT7eSBhuSbe/Diseno_sin_titulo_(8).png" alt="Control Eléctrico Libro Digital" className="w-full max-w-xs md:max-w-sm drop-shadow-2xl cursor-pointer relative z-10 hover:scale-[1.02] transition-transform duration-700" onClick={scrollToPurchase} data-id="element-85" />
          </div>

          {/* Stars + Social proof - Smaller */}
          <div className="text-center mb-8 animate-hero-text" style={{
          animationDelay: '0.9s'
        }} data-id="element-86">
            <div className="flex items-center justify-center gap-0.5 mb-2" data-id="element-87">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-88" />)}
            </div>
            <p className="text-xs font-medium text-blue-300 uppercase tracking-wide" data-id="element-89">
              Más de 3.200 profesionales ya lo usan
            </p>
          </div>

          {/* CTA Button */}
          <div className="text-center animate-hero-text" style={{
          animationDelay: '1.1s'
        }} data-id="element-90">
            <button onClick={scrollToSolution} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-2xl font-bold py-4 px-10 rounded-lg shadow-lg transition-all inline-flex items-center gap-2 animate-cta-pulse hover:scale-105" data-id="element-91">
              VER LA SOLUCIÓN
              <ArrowRight className="w-6 h-6" data-id="element-92" />
            </button>
          </div>
        </div>
      </section>

      {/* POST-HERO - Antes vs Después Comparison */}
      <section id="post-hero-section" className="py-6 px-4" style={{
      background: '#0a1929'
    }} data-id="element-93">
        <div className="container mx-auto max-w-3xl" data-id="element-94">
          <div data-reveal className={`relative bg-gradient-to-br from-gray-800/90 to-blue-900/60 border border-yellow-500/30 rounded-2xl p-5 md:p-8 shadow-2xl overflow-hidden ${postHeroVisible ? '' : ''}`} data-id="element-95">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" data-id="element-96"></div>

            {/* Comparison Table */}
            <div className="grid grid-cols-2 gap-0 mb-6" data-id="element-97">
              {/* Header */}
              <div className="bg-red-900/40 border-b border-r border-gray-600/30 p-3 text-center rounded-tl-lg" data-id="element-98">
                <p className="text-sm font-bold text-red-300 uppercase tracking-wide" data-id="element-99">
                  Instalador Común
                </p>
              </div>
              <div className="bg-yellow-600/20 border-b border-gray-600/30 p-3 text-center rounded-tr-lg" data-id="element-100">
                <p className="text-sm font-bold text-yellow-400 uppercase tracking-wide" data-id="element-101">
                  Maestro Eléctrico
                </p>
              </div>

              {/* Rows */}
              {[{
              left: 'Conecta cables por memoria',
              right: 'Entiende la lógica del circuito'
            }, {
              left: 'Copia diagramas sin comprenderlos',
              right: 'Lee e interpreta cualquier diagrama'
            }, {
              left: 'Se traba cuando algo cambia',
              right: 'Resuelve variantes con criterio'
            }, {
              left: 'Cobra lo mismo hace años',
              right: 'Toma trabajos más grandes y mejor pagos'
            }, {
              left: 'Depende de otro para validar',
              right: 'Tiene autoridad técnica frente al cliente'
            }].map((row, i) => <Fragment key={i} data-id="element-102">
                  <div className={`border-r border-gray-600/30 p-3 ${i < 4 ? 'border-b border-gray-600/20' : ''}`} data-id="element-103">
                    <div className="flex items-start gap-2" data-id="element-104">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-105" />
                      <p className="text-sm text-gray-400" data-id="element-106">{row.left}</p>
                    </div>
                  </div>
                  <div className={`p-3 ${i < 4 ? 'border-b border-gray-600/20' : ''}`} data-id="element-107">
                    <div className="flex items-start gap-2" data-id="element-108">
                      <Check className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" data-id="element-109" />
                      <p className="text-sm text-white font-medium" data-id="element-110">
                        {row.right}
                      </p>
                    </div>
                  </div>
                </Fragment>)}
            </div>

            {/* Closing statement */}
            <div className="text-center pt-4 border-t border-gray-700/50 flex flex-col items-center gap-3" data-id="element-111">
              <Lightbulb className="w-8 h-8 text-yellow-400 animate-bulb-glow" strokeWidth={1.5} data-id="element-112" />
              <p className="text-xl md:text-2xl font-extrabold text-yellow-400 leading-tight uppercase tracking-wide drop-shadow-lg" data-id="element-113">
                UN MAESTRO ELÉCTRICO RESUELVE EL CIRCUITO ANTES DE TOCARLO.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - With animated background */}
      <section id="problem-real-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-114">
        {/* Subtle pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-115"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-116">
          <h3 className={`text-3xl md:text-4xl font-extrabold text-white mb-10 text-center uppercase tracking-wide transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-117">
            EL <span className="text-yellow-400" data-id="element-118">PROBLEMA</span> REAL
          </h3>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm border-l-4 border-red-500 rounded-r-xl p-6 md:p-10 shadow-lg transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-119">
            <p className="text-lg md:text-xl text-gray-300 mb-8 font-light" data-id="element-120">
              Sabés conectar componentes individuales.
            </p>

            <div className="bg-gray-700/40 border border-gray-600/50 p-6 rounded-lg mb-10 relative overflow-hidden" data-id="element-121">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-500/50" data-id="element-122"></div>
              <div className="flex gap-4" data-id="element-123">
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" data-id="element-124" />
                <p className="text-xl md:text-2xl font-bold text-white leading-relaxed" data-id="element-125">
                  Pero el problema aparece cuando no terminás de entender la
                  lógica completa del circuito.
                </p>
              </div>
            </div>

            <div className="space-y-6 mb-10" data-id="element-126">
              {['Conocés contactores, térmicos y pulsadores por separado.', 'La dificultad real surge al integrarlos en un sistema.', 'Esa falta de visión global genera errores y retrabajo.'].map((text, i) => <div key={i} className={`flex items-start gap-4 transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`
            }} data-id="element-127">
                  <div className="w-6 h-6 rounded-full border border-gray-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-128">
                    <div className="w-2 h-2 bg-red-400 rounded-full" data-id="element-129"></div>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed" data-id="element-130">
                    {text}
                  </p>
                </div>)}
            </div>

            <div className="pt-8 border-t border-gray-700/50" data-id="element-131">
              <p className="text-lg font-medium text-gray-400 text-center italic opacity-90" data-id="element-132">
                "El problema no es falta de capacidad. Es que casi nadie enseña
                a entender la lógica real del control eléctrico."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ¿TE PASA ESTO? - With animated background */}
      <section id="fears-section" className="py-8 px-4 bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950 animate-gradient-slow relative overflow-hidden" data-id="element-133">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-134"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-135">
          <div className={`text-center mb-12 transition-all duration-700 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-136">
            <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-4 uppercase" data-id="element-137">
              ¿TE <span className="text-yellow-400" data-id="element-138">PASA</span> ESTO?
            </h3>
            <p className="text-lg text-gray-400" data-id="element-139">
              Si trabajás con tableros, motores y circuitos, esto es para vos.
            </p>
          </div>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg transition-all duration-700 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-140">
            <div className="space-y-6" data-id="element-141">
              {fearItems.map((item, i) => <div key={i} className={`flex items-start gap-4 pb-6 ${i < fearItems.length - 1 ? 'border-b border-gray-700' : ''} transition-all duration-500 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`,
              animationFillMode: 'forwards'
            }} data-id="element-142">
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-143">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-144" />
                  </div>
                  <div data-id="element-145">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-146">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-147">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 text-center" data-id="element-148">
              <p className="text-xl font-medium text-gray-300 mb-2" data-id="element-149">
                Ahí aparece la inseguridad.
              </p>
              <p className="text-xl font-bold text-white" data-id="element-150">
                Y en control eléctrico, la inseguridad{' '}
                <span className="text-yellow-400" data-id="element-151">se paga caro</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EL PROBLEMA NO SOS VOS - With animated background */}
      <section id="problem-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-152">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-153"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-154">
          <div className={`text-center mb-10 transition-all duration-700 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-155">
            <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-2 uppercase" data-id="element-156">
              EL PROBLEMA <span className="text-yellow-400" data-id="element-157">NO SOS VOS</span>
            </h3>
            <p className="text-lg text-gray-400" data-id="element-158">
              La mayoría aprende partes sueltas, pero no el sistema completo.
            </p>
          </div>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg transition-all duration-700 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-159">
            <div className="space-y-5" data-id="element-160">
              {problemItems.map((item, i) => <div key={i} className={`flex items-start gap-4 ${i < problemItems.length - 1 ? 'pb-5 border-b border-gray-700' : ''} transition-all duration-500 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`,
              animationFillMode: 'forwards'
            }} data-id="element-161">
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-162">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-163" />
                  </div>
                  <div data-id="element-164">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-165">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-166">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 text-center" data-id="element-167">
              <p className="text-lg font-bold text-white" data-id="element-168">
                En casi ningún lado te enseñan a pensar el control eléctrico
                como un{' '}
                <span className="text-yellow-400" data-id="element-169">sistema completo</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="py-8 px-4 bg-gradient-to-b from-blue-900 to-blue-800 text-white relative overflow-hidden" data-id="element-170">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" data-id="element-171"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-172">
          <div className="text-center mb-12" data-id="element-173">
            <span className="bg-yellow-500 text-blue-900 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-174">
              La Solución Definitiva
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 uppercase" data-id="element-175">
              AHÍ NACE{' '}
              <span className="text-yellow-400" data-id="element-176">CONTROL ELÉCTRICO</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto" data-id="element-177">
              Un manual práctico creado para ayudarte a:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-178">
            <div className="space-y-4" data-id="element-179">
              {['Entender la lógica de los circuitos de mando', 'Leer diagramas con seguridad', 'Aplicar lo aprendido en motores y tableros reales', 'Dejar de memorizar esquemas y empezar a comprenderlos'].map((item, index) => <div key={index} className="flex items-start gap-3 bg-blue-800/50 p-4 rounded-lg border border-blue-700" data-id="element-180">
                  <Check className="text-yellow-400 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-181" />
                  <span className="text-lg" data-id="element-182">{item}</span>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-183">
              <video src="https://cdn.magicpatterns.com/uploads/rHCx25VPdb8sHH7a3Qn4xF/0312 (1)(1).mp4" className="w-full max-w-sm drop-shadow-2xl rounded-lg" autoPlay loop muted playsInline data-id="element-184" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 text-center max-w-3xl mx-auto" data-id="element-185">
            <p className="text-xl italic leading-relaxed" data-id="element-186">
              "Este material funciona como un traductor entre el diagrama y el
              tablero real."
            </p>
          </div>
        </div>
      </section>

      {/* DECISION BLOCK */}
      <section id="decision-section" className="py-8 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden" data-id="element-187">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" data-id="element-188"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-189">
          <div className="text-center mb-12" data-id="element-190">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight uppercase" data-id="element-191">
              <span className="text-yellow-400" data-id="element-192">CONTROL ELÉCTRICO</span>
            </h2>
            <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-193">
              EL CRITERIO QUE TE FALTABA EN TABLEROS Y MOTORES
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-12" data-id="element-194">
            <div className="space-y-3" data-id="element-195">
              {decisionItems.map((item, index) => <div key={index} className={`flex items-center gap-4 p-4 rounded-lg bg-blue-800/40 border border-blue-600/40 transition-all duration-300 hover:bg-blue-700/50 hover:border-blue-500/50 hover:translate-y-[-2px] hover:shadow-lg ${decisionVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${index * 80}ms`,
              animationFillMode: 'forwards'
            }} data-id="element-196">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="element-197">
                    <item.icon className="w-5 h-5 text-yellow-400" data-id="element-198" />
                  </div>
                  <span className="text-lg text-gray-100" data-id="element-199">
                    {item.text.split(item.keyword).map((part, i, arr) => <span key={i} data-id="element-200">
                        {part}
                        {i < arr.length - 1 && <span className="text-blue-300" data-id="element-201">{item.keyword}</span>}
                      </span>)}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="text-center" data-id="element-202">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-2xl font-extrabold py-5 px-12 rounded-lg shadow-2xl hover:shadow-yellow-500/30 transition-all transform hover:scale-105" data-id="element-203">
              SÍ, QUIERO LA OFERTA AHORA
            </button>
          </div>
        </div>
      </section>

      {/* BONUSES - Carousel */}
      <section id="bonuses" className="py-8 px-4 bg-black" data-id="element-204">
        <div className="container mx-auto max-w-6xl" data-id="element-205">
          <div className="text-center mb-12" data-reveal data-id="element-206">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight relative inline-block" data-id="element-207">
              SI ACCEDÉS HOY, TE LLEVÁS ESTOS 4 BONUS DE REGALO
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" data-id="element-208"></span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6 mt-4" data-id="element-209">
              Complementos perfectos para tu crecimiento profesional
            </p>
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-400 px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide border border-yellow-500/30" data-id="element-210">
              <Clock className="w-4 h-4" data-id="element-211" />
              GRATIS POR TIEMPO LIMITADO
            </div>
          </div>

          {/* CSS Animation Auto-Scroll Carousel with drag support */}
          <div ref={marqueeContainerRef} className={`cursor-grab active:cursor-grabbing ${marqueePaused ? 'overflow-x-auto' : 'overflow-hidden'}`} style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} onMouseDown={e => handleDragStart(e.clientX)} onMouseMove={e => handleDragMove(e.clientX)} onMouseUp={handleDragEnd} onMouseLeave={handleDragEnd} onTouchStart={e => handleDragStart(e.touches[0].clientX)} onTouchMove={e => handleDragMove(e.touches[0].clientX)} onTouchEnd={handleDragEnd} data-id="element-212">
            <div className={`bonus-marquee-track flex gap-6 ${marqueePaused ? 'bonus-marquee-paused' : ''}`} data-id="element-213">
              {/* Render bonuses twice for seamless infinite loop */}
              {[...Array(2)].map((_, setIdx) => [{
              title: 'CURSO DE PANELES SOLARES',
              desc: 'Más de 50 clases grabadas para sumar una nueva especialidad altamente demandada en energía solar.',
              img: 'https://cdn.magicpatterns.com/uploads/w2rTPZ1b9h5D7JWz5dRRda/usa-la-imagen-adjunta-como-referencia-vi_LIZOyjwkQ0eu3ZbUzqbGFw_459-daMfTbW7XMmyjjHVYA_2k.png',
              value: '$32.500 ARS',
              exclusive: true,
              label: 'BONO EXCLUSIVO'
            }, {
              title: 'EFICIENCIA EN USO INDUSTRIAL DE MOTORES ELÉCTRICOS',
              desc: 'Material técnico orientado al uso correcto de motores eléctricos en entornos industriales.',
              img: 'https://cdn.magicpatterns.com/uploads/5PkPiEeeC1MZhuP4akxxtH/usa-la-imagen-adjunta-como-referencia-vi_960JGUsASvyGmMo1qjaiQA_Xooqizw8RGCTh6JN-ZnLzQ_2k.png',
              value: '$14.990 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }, {
              title: 'PLANTILLAS DE PRESUPUESTO EDITABLE',
              desc: 'Herramienta editable diseñada para trabajos eléctricos profesionales.',
              img: 'https://cdn.magicpatterns.com/uploads/3azTVPVMVUPk4VXFbkPVpq/usa-la-imagen-adjunta-como-referencia-vi_-b3FXSAQQUqRigIWxf5EaA_dtQ1YssmQKOK9b7HVFcPdw_2k.png',
              value: '$6.990 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }, {
              title: 'PROTOCOLO DE VERIFICACIÓN PREVIA A LA ENERGIZACIÓN',
              desc: 'Documento técnico estructurado que establece criterios de verificación.',
              img: 'https://cdn.magicpatterns.com/uploads/uSM2nfSkez39RWA6JhZqX8/usa-la-imagen-adjunta-como-referencia-vi_ZyEMBZPbQfq51RIdYVrp5w_iCvQimVBSW2XAs6iwzcoJQ_2k.png',
              value: '$9.990 ARS',
              exclusive: false,
              label: 'HOY: GRATIS'
            }].map((bonus, i) => <div key={`${setIdx}-${i}`} onClick={scrollToPurchase} className="cursor-pointer flex-shrink-0 w-[280px] md:w-[300px]" data-id="element-214">
                    <div className={`bg-gray-950 rounded-xl overflow-hidden border-2 h-full flex flex-col ${bonus.exclusive ? 'border-yellow-400 shadow-xl shadow-yellow-500/10' : 'border-yellow-600/50 shadow-lg'}`} data-id="element-215">
                      <div className={`${bonus.exclusive ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black' : 'bg-gradient-to-r from-yellow-700 to-yellow-600 text-black'} px-4 py-2 text-center font-bold text-sm uppercase`} data-id="element-216">
                        {bonus.label}
                      </div>
                      <div className="p-5 flex flex-col flex-grow" data-id="element-217">
                        <div className="aspect-[3/4] mb-4 bg-black rounded-lg overflow-hidden relative" data-id="element-218">
                          {bonus.exclusive && <div className="absolute top-2 left-2 bg-yellow-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded z-10 tracking-wider" data-id="element-219">
                              BONO PREMIUM
                            </div>}
                          <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" data-id="element-220" />
                        </div>
                        <h3 className={`font-bold text-sm ${bonus.exclusive ? 'text-yellow-400' : 'text-white'} mb-3 leading-tight uppercase`} data-id="element-221">
                          {bonus.title}
                        </h3>
                        <p className="text-sm text-gray-400 mb-4 leading-relaxed flex-grow" data-id="element-222">
                          {bonus.desc}
                        </p>
                        <p className="text-sm text-gray-500 mb-2" data-id="element-223">
                          Valor:{' '}
                          <span className={`line-through ${bonus.exclusive ? 'text-red-400 font-medium' : 'text-gray-500'}`} data-id="element-224">
                            {bonus.value}
                          </span>
                        </p>
                        <div className={`${bonus.exclusive ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg px-3 py-2 mt-auto`} data-id="element-225">
                          <p className={`text-sm font-bold ${bonus.exclusive ? 'text-yellow-400' : 'text-green-400'}`} data-id="element-226">
                            ✓ Incluido sin costo adicional
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>))}
            </div>
          </div>
          <p className="text-center text-sm mt-3" data-id="element-227">
            <span className="text-yellow-400" data-id="element-228">←</span>
            <span className="text-gray-400 mx-2" data-id="element-229">
              DESLIZÁ PARA VER MÁS BONOS
            </span>
            <span className="text-yellow-400" data-id="element-230">→</span>
          </p>
        </div>
      </section>

      {/* LO QUE TE LLEVÁS */}
      <section id="benefits-section" className="py-8 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-231">
        <div className="container mx-auto max-w-5xl" data-id="element-232">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-10 text-center" data-id="element-233">
            Lo que te llevás con Control Eléctrico
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-234">
            {[{
            icon: Shield,
            title: 'CRITERIO Y SEGURIDAD',
            items: ['Más seguridad frente a los diagramas', 'Más criterio técnico'],
            tag: 'BASE TÉCNICA'
          }, {
            icon: Target,
            title: 'MENOS ERRORES',
            items: ['Menos errores', 'Menos dudas'],
            tag: 'MENOS RETRABAJO'
          }, {
            icon: TrendingUp,
            title: 'PERFIL PROFESIONAL',
            items: ['Más confianza profesional'],
            tag: 'MEJOR IMAGEN ANTE EL CLIENTE'
          }].map((card, i) => <div key={i} className={`bg-white border border-blue-200 rounded-xl p-6 shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            animationDelay: `${i * 100}ms`,
            animationFillMode: 'forwards'
          }} data-id="element-235">
                <div className="flex items-center gap-2 mb-4" data-id="element-236">
                  <card.icon className="w-5 h-5 text-blue-600" data-id="element-237" />
                  <h3 className="font-bold text-gray-900" data-id="element-238">{card.title}</h3>
                </div>
                <div className="space-y-3 mb-4" data-id="element-239">
                  {card.items.map((item, j) => <div key={j} className="flex items-start gap-2" data-id="element-240">
                      <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-241" />
                      <span className="text-gray-700 text-sm" data-id="element-242">{item}</span>
                    </div>)}
                </div>
                <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full" data-id="element-243">
                  {card.tag}
                </span>
              </div>)}
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-8 rounded-xl text-center shadow-lg" data-id="element-244">
            <p className="text-xl font-semibold leading-relaxed" data-id="element-245">
              Invertís una vez en criterio. Eso se traduce en más trabajos,
              mejores decisiones y clientes que confían en vos.
            </p>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION - TWO CARDS */}
      <section id="compra" className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-246">
        <div className="container mx-auto max-w-2xl space-y-12" data-id="element-247">
          {/* CARD 1: MAESTRO ELÉCTRICO — Deep Industrial Blue + Glow */}
          <div className="bg-[#0a1a3a] rounded-2xl border-[3px] border-blue-400/70 relative overflow-hidden shadow-2xl animate-blue-glow-pulse" data-reveal data-id="element-248">
            {/* Blue Banner — Updated text */}
            <div className="bg-blue-600 text-white font-extrabold py-3 px-4 text-center text-sm uppercase tracking-wider" data-id="element-249">
              OFERTA POR TIEMPO LIMITADO
            </div>

            <div className="p-6 md:p-8" data-id="element-250">
              {/* Video */}
              <div className="mb-6 rounded-lg overflow-hidden shadow-md border border-blue-500/30" data-id="element-251">
                <video src="https://cdn.magicpatterns.com/uploads/5LmA44anJeCQ6Uq6aCcidD/Diseno_sin_titulo_(9).undefined" className="w-full h-auto" autoPlay loop muted playsInline data-id="element-252" />
              </div>

              {/* NEW TITLE — Inserted between video and price */}
              <h3 className="text-2xl md:text-3xl font-extrabold text-center uppercase tracking-wide mb-6 text-white" data-id="element-253">
                MAESTRO ELÉCTRICO + 4 BONOS DE REGALO
              </h3>

              {/* Pricing block — adjusted for dark bg */}
              <div className="text-center mb-1" data-id="element-254">
                <p className="text-sm text-gray-400 font-medium" data-id="element-255">
                  Antes{' '}
                  <span className="line-through text-gray-500" data-id="element-256">
                    $49.900 ARS
                  </span>
                </p>
              </div>
              <div className="text-center mb-5" data-id="element-257">
                <p className="text-5xl md:text-6xl font-extrabold text-white leading-none" data-id="element-258">
                  $19.990{' '}
                  <span className="text-lg font-normal text-gray-400" data-id="element-259">ARS</span>
                </p>
              </div>

              {/* Installments badge */}
              <div className="flex justify-center mb-6" data-id="element-260">
                <div className="badge-shimmer bg-green-900/40 text-green-400 font-semibold px-5 py-2 rounded-lg text-sm flex items-center gap-2 border border-green-500/30" data-id="element-261">
                  <CreditCard className="w-4 h-4" data-id="element-262" />
                  Hasta 3 cuotas sin interés
                </div>
              </div>

              {/* Urgency Header — Compact black bar */}
              <div className="bg-black rounded-lg px-4 py-2 mb-3 text-center border border-yellow-500/60" data-id="element-263">
                <p className="text-xs md:text-sm font-bold text-white uppercase tracking-wide" data-id="element-264">
                  OFERTA VÁLIDA SOLO POR HOY:{' '}
                  <span className="text-yellow-400 font-extrabold" data-id="element-265">
                    {weekdayDate}
                  </span>
                </p>
              </div>

              {/* Urgency Counters Row — viewers + cupos */}
              <div className="bg-black rounded-xl px-4 py-3 mb-3 border border-gray-800 flex items-center justify-center gap-4" data-id="element-266">
                <div className="flex items-center gap-2" data-id="element-267">
                  <Eye className="w-4 h-4 text-gray-400" data-id="element-268" />
                  <span className="text-lg font-bold" style={{
                  color: '#00FF00',
                  textShadow: '0 0 8px rgba(0,255,0,0.6), 0 0 20px rgba(0,255,0,0.3)'
                }} data-id="element-269">
                    {viewers}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-270">
                    Personas mirando
                    <br data-id="element-271" />
                    en este momento
                  </span>
                </div>
                <div className="w-px h-8 bg-gray-700" data-id="element-272"></div>
                <div className="flex items-center gap-2" data-id="element-273">
                  <Ticket className="w-4 h-4 text-gray-400" data-id="element-274" />
                  <span className="text-lg font-bold animate-red-glow-pulse" style={{
                  color: '#FF0000',
                  textShadow: '0 0 8px rgba(255,0,0,0.6), 0 0 20px rgba(255,0,0,0.3)'
                }} data-id="element-275">
                    {urgencySpots}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-276">
                    Cupos
                    <br data-id="element-277" />
                    disponibles
                  </span>
                </div>
              </div>

              {/* Professional Counter — Dark style, green glow */}
              <div className="bg-black rounded-xl px-6 py-4 mb-6 border border-gray-800" data-id="element-278">
                <div className="flex flex-col items-center" data-id="element-279">
                  <span className="text-4xl font-extrabold tracking-tight" style={{
                  color: '#00FF00',
                  textShadow: '0 0 10px rgba(0,255,0,0.6), 0 0 30px rgba(0,255,0,0.3), 0 0 50px rgba(0,255,0,0.15)'
                }} data-id="element-280">
                    {mainBuyers}
                  </span>
                  <span className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.15em] mt-1" data-id="element-281">
                    PROFESIONALES YA ACCEDIERON
                  </span>
                </div>
              </div>

              {/* Main CTA Button */}
              <button onClick={handlePurchaseClick} className="btn-shine w-full bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-lg font-extrabold py-4 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mb-6" data-id="element-282">
                SÍ, QUIERO LA OFERTA AHORA
                <ArrowRight className="w-5 h-5" data-id="element-283" />
              </button>

              {/* Payment methods */}
              <div className="text-center mb-6" data-id="element-284">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-285">
                  MEDIOS DE PAGO
                </p>
                <div className="flex justify-center" data-id="element-286">
                  <img src="https://cdn.magicpatterns.com/uploads/gxJ13jdAqycrJ4WCnDAsQm/mercadopago2.jpg" alt="Mercado Pago y tarjetas" className="w-full max-w-[320px] object-contain rounded-lg" data-id="element-287" />
                </div>
              </div>
            </div>

            {/* Trust badges on dark background — identical to Upsell */}
            <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-6 border-t-2 border-gray-700" data-id="element-288">
              <div className="grid grid-cols-2 gap-4 items-center" data-id="element-289">
                <div className="flex flex-col items-center gap-2" data-id="element-290">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía de 7 días" className="w-full max-w-[120px] object-contain" data-id="element-291" />
                  <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-292">
                    GARANTÍA DE SATISFACCIÓN
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-293">
                  <img src="https://cdn.magicpatterns.com/uploads/bmodW1kGNGDhBh2dosY7h5/safe-purchase-7779878_1280.png" alt="Compra Segura" className="w-full max-w-[156px] object-contain" data-id="element-294" />
                  <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-295">
                    COMPRA 100% SEGURA
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CARD 2: COMBO / UPSELL */}
          <div className="bg-[#0a1a3a] rounded-2xl border-[3px] border-yellow-400 relative mb-6 overflow-hidden animate-card-glow shadow-2xl" data-reveal data-id="element-296">
            <div className="bg-yellow-400 text-gray-900 font-extrabold py-3 px-4 text-center text-sm uppercase tracking-wider" data-id="element-297">
              ESPECIALIZACIÓN INDUSTRIAL COMPLETA
            </div>
            <p className="text-center font-bold text-white bg-gray-900 py-2 px-4 text-xs md:text-sm uppercase tracking-wide" data-id="element-298">
              MAESTRO ELÉCTRICO + PLC MASTER
            </p>

            {/* Product Image — with subtle vibration */}
            <div className="relative bg-gradient-to-b from-gray-100 to-gray-200 pt-4 px-4 pb-4 border-b border-gray-300" data-id="element-299">
              <img src="https://cdn.magicpatterns.com/uploads/tvNcA7mMJGGXnANf1wZviT/aasdadasdasd.png" alt="Combo Maestro Eléctrico + Especialización Industrial" className="w-full h-auto object-cover rounded-lg shadow-inner animate-subtle-vibrate" data-id="element-300" />
            </div>

            {/* Circular Offer Badge */}
            <div className="flex justify-center -mt-14 mb-4 relative z-10" data-id="element-301">
              <div className="relative" data-id="element-302">
                {/* Rotating outer ring */}
                <div className="absolute inset-0 rounded-full animate-rotate-border" style={{
                background: 'conic-gradient(from 0deg, #fbbf24, #f59e0b, #d97706, #fbbf24, #f59e0b, #d97706, #fbbf24)',
                padding: '3px',
                borderRadius: '9999px'
              }} data-id="element-303"></div>
                {/* Inner circle */}
                <div className="relative w-44 h-44 md:w-52 md:h-52 rounded-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-4 border-yellow-400 flex flex-col items-center justify-center text-center p-4 animate-offer-circle-pulse" style={{
                margin: '3px'
              }} data-id="element-304">
                  <span className="text-yellow-400 font-extrabold text-xs md:text-sm uppercase leading-tight tracking-wide" data-id="element-305">
                    ¡COMBO TOTAL
                  </span>
                  <span className="text-yellow-400 font-extrabold text-xs md:text-sm uppercase leading-tight tracking-wide" data-id="element-306">
                    PROFESIONAL!
                  </span>
                  <div className="w-12 h-px bg-yellow-500/50 my-1.5" data-id="element-307"></div>
                  <span className="text-white text-[10px] md:text-[11px] font-bold uppercase leading-tight" data-id="element-308">
                    INCLUYE:
                  </span>
                  <span className="text-gray-300 text-[9px] md:text-[10px] font-medium leading-tight mt-0.5" data-id="element-309">
                    2 MANUALES + 8 BONOS
                  </span>
                  <span className="text-gray-300 text-[9px] md:text-[10px] font-medium leading-tight" data-id="element-310">
                    DE REGALO
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-8 pt-2" data-id="element-311">
              {/* Pricing block — Premium gold gradient title + green price */}
              <div className="text-center mb-6" data-id="element-312">
                <p className="text-xl md:text-2xl font-extrabold mb-3 uppercase tracking-wide text-gradient-gold leading-tight" data-id="element-313">
                  MAESTRO ELÉCTRICO + PLC MASTER + 8 BONOS DE REGALO
                </p>
                <p className="text-5xl md:text-6xl font-extrabold leading-none mb-2" style={{
                color: '#00FF00',
                textShadow: '0 0 10px rgba(0,255,0,0.5), 0 0 30px rgba(0,255,0,0.2)'
              }} data-id="element-314">
                  $29.990{' '}
                  <span className="text-2xl font-normal text-gray-400" data-id="element-315">
                    ARS
                  </span>
                </p>
                <p className="text-lg text-gray-400 font-medium" data-id="element-316">
                  Antes{' '}
                  <span className="line-through text-gray-500" data-id="element-317">
                    $99.980 ARS
                  </span>
                </p>
                <p className="text-sm font-bold text-red-500 uppercase tracking-wide mt-1" data-id="element-318">
                  AHORRÁ MÁS DEL 60%
                </p>
              </div>

              {/* Installments badge */}
              <div className="flex justify-center mb-6" data-id="element-319">
                <div className="badge-shimmer bg-green-900/40 text-green-400 font-semibold px-5 py-2 rounded-lg text-sm flex items-center gap-2 border border-green-500/30" data-id="element-320">
                  <CreditCard className="w-4 h-4" data-id="element-321" />
                  Hasta 3 cuotas sin interés
                </div>
              </div>

              {/* Urgency Header — Compact black bar (identical to Card 1) */}
              <div className="bg-black rounded-lg px-4 py-2 mb-3 text-center border border-yellow-500/60" data-id="element-322">
                <p className="text-xs md:text-sm font-bold text-white uppercase tracking-wide" data-id="element-323">
                  OFERTA VÁLIDA SOLO POR HOY:{' '}
                  <span className="text-yellow-400 font-extrabold" data-id="element-324">
                    {weekdayDate}
                  </span>
                </p>
              </div>

              {/* Urgency Counters Row — viewers + cupos (identical to Card 1) */}
              <div className="bg-black rounded-xl px-4 py-3 mb-3 border border-gray-800 flex items-center justify-center gap-4" data-id="element-325">
                <div className="flex items-center gap-2" data-id="element-326">
                  <Eye className="w-4 h-4 text-gray-400" data-id="element-327" />
                  <span className="text-lg font-bold" style={{
                  color: '#00FF00',
                  textShadow: '0 0 8px rgba(0,255,0,0.6), 0 0 20px rgba(0,255,0,0.3)'
                }} data-id="element-328">
                    {viewers}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-329">
                    Personas mirando
                    <br data-id="element-330" />
                    en este momento
                  </span>
                </div>
                <div className="w-px h-8 bg-gray-700" data-id="element-331"></div>
                <div className="flex items-center gap-2" data-id="element-332">
                  <Ticket className="w-4 h-4 text-gray-400" data-id="element-333" />
                  <span className="text-lg font-bold" style={{
                  color: '#FF2020',
                  textShadow: '0 0 8px rgba(255,32,32,0.6), 0 0 20px rgba(255,32,32,0.3)'
                }} data-id="element-334">
                    {upsellSpots}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium uppercase leading-tight" data-id="element-335">
                    Cupos promocionales
                    <br data-id="element-336" />
                    disponibles
                  </span>
                </div>
              </div>

              {/* Professional Counter — Dark style, green glow (identical to Card 1) */}
              <div className="bg-black rounded-xl px-6 py-4 mb-6 border border-gray-800" data-id="element-337">
                <div className="flex flex-col items-center" data-id="element-338">
                  <span className="text-4xl font-extrabold tracking-tight" style={{
                  color: '#00FF00',
                  textShadow: '0 0 10px rgba(0,255,0,0.6), 0 0 30px rgba(0,255,0,0.3), 0 0 50px rgba(0,255,0,0.15)'
                }} data-id="element-339">
                    {comboBuyers}
                  </span>
                  <span className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.15em] mt-1" data-id="element-340">
                    PROFESIONALES YA ACCEDIERON
                  </span>
                </div>
              </div>

              {/* Main CTA Button */}
              <button onClick={handleComboPurchaseClick} className="btn-shine w-full bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-lg md:text-xl font-extrabold py-5 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mb-6" data-id="element-341">
                SÍ, QUIERO EL COMBO COMPLETO
                <ArrowRight className="w-6 h-6" data-id="element-342" />
              </button>

              {/* Payment methods */}
              <div className="text-center mb-6" data-id="element-343">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-344">
                  MEDIOS DE PAGO
                </p>
                <div className="flex justify-center" data-id="element-345">
                  <img src="https://cdn.magicpatterns.com/uploads/gxJ13jdAqycrJ4WCnDAsQm/mercadopago2.jpg" alt="Mercado Pago y tarjetas" className="w-full max-w-[320px] object-contain" data-id="element-346" />
                </div>
              </div>

              {/* Trust footer — unified (identical to Card 1) */}
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-blue-800/50" data-id="element-347">
                <div className="flex items-center gap-1.5" data-id="element-348">
                  <Shield className="w-4 h-4 text-green-400" data-id="element-349" />
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide" data-id="element-350">
                    Pago 100% encriptado
                  </span>
                </div>
                <div className="w-px h-5 bg-blue-800/50" data-id="element-351"></div>
                <div className="flex items-center gap-1.5" data-id="element-352">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía" className="w-5 h-5 object-contain" data-id="element-353" />
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide" data-id="element-354">
                    Garantía sin riesgo
                  </span>
                </div>
              </div>
            </div>

            {/* Trust badges on dark background */}
            <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-6 border-t-2 border-gray-700" data-id="element-355">
              <div className="grid grid-cols-2 gap-4 items-center" data-id="element-356">
                <div className="flex flex-col items-center gap-2" data-id="element-357">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía de 7 días" className="w-full max-w-[120px] object-contain" data-id="element-358" />
                  <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-359">
                    GARANTÍA DE SATISFACCIÓN
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-360">
                  <img src="https://cdn.magicpatterns.com/uploads/bmodW1kGNGDhBh2dosY7h5/safe-purchase-7779878_1280.png" alt="Compra Segura" className="w-full max-w-[156px] object-contain" data-id="element-361" />
                  <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-362">
                    COMPRA 100% SEGURA
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* MercadoLíder Platinum Trust Seal */}
          <div className="flex justify-center" data-id="element-363">
            <img src="https://cdn.magicpatterns.com/uploads/wihdMwuqBpQKvri8XHgUZZ/Gemini_Generated_Image_jbp7w2jbp7w2jbp7.png" alt="MercadoLíder Platinum - 8413 ventas" className="w-full max-w-2xl object-contain" data-id="element-364" />
          </div>

          {/* Post-Purchase Benefits Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center py-4" data-id="element-365">
            <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-366">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-367">
                <Check className="w-4 h-4" data-id="element-368" />
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-369">
                Acceso de por vida con actualizaciones
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-370">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-371">
                <Download className="w-4 h-4" data-id="element-372" />
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-373">
                Acceso instantáneo luego del pago
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-374">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-375">
                <Headphones className="w-4 h-4" data-id="element-376" />
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-377">
                Asistencia técnica incluida
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-8 px-4 bg-white" data-id="element-378">
        <div className="container mx-auto max-w-6xl" data-id="element-379">
          <div className="text-center mb-12" data-id="element-380">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-381">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-382">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="space-y-16" data-id="element-383">
            <div className="flex justify-center" data-id="element-384">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl" data-id="element-385">
                <video src="https://cdn.magicpatterns.com/uploads/2sNKU8uzEEtWZTuVPY2CeA/0126.undefined" className="w-full" autoPlay loop muted playsInline data-id="element-386" />
              </div>
            </div>
            <ImageCarousel images={contentImages} data-id="element-387" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - WhatsApp style, right before FAQ */}
      <section className="py-8 px-4 bg-gradient-to-b from-gray-100 to-gray-50" data-id="element-388">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-389">
          <div className="text-center mb-8" data-id="element-390">
            <div className="flex items-center justify-center gap-1 mb-2" data-id="element-391">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-392" />)}
            </div>
            <h2 className="text-lg font-bold text-gray-800 uppercase" data-id="element-393">
              MÁS DE 3.200 ELECTRICISTAS SATISFECHOS
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-394">
              4,9/5 — 487 reseñas verificadas
            </p>
          </div>

          {/* WhatsApp-style chat bubbles */}
          <div className="space-y-4 mb-8" data-id="element-395">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <div key={i} className="flex items-start gap-3" data-id="element-396">
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-397">
                  {t.name.charAt(0)}
                </div>
                <div className="flex-grow" data-id="element-398">
                  <div className="flex items-center gap-2 mb-1" data-id="element-399">
                    <span className="font-bold text-sm text-gray-800" data-id="element-400">
                      {t.name}
                    </span>
                    {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-401">
                        ✓ Verificado
                      </span>}
                  </div>
                  <div className="chat-bubble" data-id="element-402">
                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-403">
                      {t.msg}
                    </p>
                    <p className="text-[10px] text-gray-400 text-right mt-1" data-id="element-404">
                      {t.time}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-405">
              <button onClick={showMoreTestimonials} className="btn-shine bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 font-medium py-2.5 px-6 rounded-lg transition-colors text-sm" data-id="element-406">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-407" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-8 px-4 bg-white" data-id="element-408">
        <div className="container mx-auto max-w-3xl" data-id="element-409">
          <div className="text-center mb-12" data-id="element-410">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-411">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-412">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-413">
            <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-414" />
            <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-415" />
            <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-416" />
            <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-417" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-418" />
            <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-419" />
            <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-420" />
            <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-421" />
            <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-422" />
            <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-423" />
            <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-424" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-sm" data-id="element-425">
        <div className="container mx-auto" data-id="element-426">
          <p data-id="element-427">
            &copy; {new Date().getFullYear()} Control Eléctrico. Todos los
            derechos reservados.
          </p>
          <p className="mt-2" data-id="element-428">
            Diagramas y circuitos de mando para profesionales.
          </p>
        </div>
      </footer>
    </div>;
}